const DB_NAME = 'mobile_orders_catalog_db'
const DB_VERSION = 1
const STORE_PRODUCTS = 'products'
const STORE_META = 'meta'

let dbPromise = null

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Operación IndexedDB fallida'))
  })
}

function transactionToPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(true)
    transaction.onerror = () => reject(transaction.error || new Error('Transacción IndexedDB fallida'))
    transaction.onabort = () => reject(transaction.error || new Error('Transacción IndexedDB abortada'))
  })
}

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
        const products = db.createObjectStore(STORE_PRODUCTS, { keyPath: 'key' })
        products.createIndex('sellerId', 'sellerId', { unique: false })
        products.createIndex('sellerCode', ['sellerId', 'normalizedCode'], { unique: false })
        products.createIndex('sellerName', ['sellerId', 'normalizedName'], { unique: false })
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' })
      }
    }

    request.onsuccess = event => {
      const db = event.target.result
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      resolve(db)
    }
    request.onerror = () => {
      dbPromise = null
      reject(request.error || new Error('No se pudo abrir IndexedDB'))
    }
  })

  return dbPromise
}

function createStoredProduct(sellerId, product) {
  const parsedSellerId = parseInt(sellerId, 10)
  const productId = parseInt(product.id, 10)
  return {
    key: `${parsedSellerId}|${productId}`,
    sellerId: parsedSellerId,
    productId,
    normalizedCode: normalize(product.code),
    normalizedName: normalize(product.name),
    product,
  }
}

function productMatchesFilters(product, priceListId, onlyWithPrice, onlyWithStock) {
  if (onlyWithStock && !(parseFloat(product.stock) > 0)) return false
  if (onlyWithPrice && priceListId) {
    const price = product._offlinePrices && product._offlinePrices[priceListId]
    if (!price || !(parseFloat(price.amount_total) > 0)) return false
  }
  return true
}

const CatalogStorage = {
  async replaceProducts(sellerId, products) {
    try {
      const parsedSellerId = parseInt(sellerId, 10)
      const entries = (products || [])
        .filter(product => product && Number.isFinite(parseInt(product.id, 10)))
        .map(product => createStoredProduct(parsedSellerId, product))
      // Una respuesta vacía inesperada no debe reemplazar un catálogo válido.
      if (!Number.isFinite(parsedSellerId) || !entries.length) return false
      const db = await openDb()
      const transaction = db.transaction([STORE_PRODUCTS, STORE_META], 'readwrite')
      const store = transaction.objectStore(STORE_PRODUCTS)
      const index = store.index('sellerId')

      // El borrado y la escritura viven en la misma transacción: IndexedDB
      // confirma el catálogo nuevo completo o conserva el anterior completo.
      const cursorRequest = index.openCursor(IDBKeyRange.only(parsedSellerId))
      cursorRequest.onsuccess = event => {
        const cursor = event.target.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
          return
        }
        entries.forEach(entry => store.put(entry))
        transaction.objectStore(STORE_META).put({
          key: `catalog_${parsedSellerId}`,
          sellerId: parsedSellerId,
          productsCount: entries.length,
          updatedAt: Date.now(),
        })
      }
      cursorRequest.onerror = () => transaction.abort()

      await transactionToPromise(transaction)
      return true
    } catch (error) {
      console.warn('[CatalogStorage] No se pudo reemplazar el catálogo:', error)
      return false
    }
  },

  async getMeta(sellerId) {
    try {
      const db = await openDb()
      const transaction = db.transaction(STORE_META, 'readonly')
      return await requestToPromise(transaction.objectStore(STORE_META).get(`catalog_${parseInt(sellerId, 10)}`))
    } catch (error) {
      return null
    }
  },

  async hasProducts(sellerId) {
    const meta = await this.getMeta(sellerId)
    return !!(meta && meta.productsCount > 0)
  },

  async getAllProducts(sellerId) {
    try {
      const db = await openDb()
      const transaction = db.transaction(STORE_PRODUCTS, 'readonly')
      const rows = await requestToPromise(
        transaction.objectStore(STORE_PRODUCTS).index('sellerId').getAll(parseInt(sellerId, 10))
      )
      return (rows || []).map(row => row.product)
    } catch (error) {
      return []
    }
  },

  async getProduct(sellerId, productId) {
    try {
      const db = await openDb()
      const transaction = db.transaction(STORE_PRODUCTS, 'readonly')
      const row = await requestToPromise(
        transaction.objectStore(STORE_PRODUCTS).get(`${parseInt(sellerId, 10)}|${parseInt(productId, 10)}`)
      )
      return row ? row.product : null
    } catch (error) {
      return null
    }
  },

  async searchProducts(sellerId, query, options = {}) {
    try {
      const parsedSellerId = parseInt(sellerId, 10)
      const normalizedQuery = normalize(query)
      const words = normalizedQuery.split(/\s+/).filter(Boolean)
      const limit = Math.max(1, parseInt(options.limit, 10) || 40)
      const offset = Math.max(0, parseInt(options.offset, 10) || 0)
      if (!words.length) return { items: [], hasMore: false, nextOffset: 0 }

      const db = await openDb()
      const exactTransaction = db.transaction(STORE_PRODUCTS, 'readonly')
      const exactRow = await requestToPromise(
        exactTransaction.objectStore(STORE_PRODUCTS).index('sellerCode').get([parsedSellerId, normalizedQuery])
      )
      const exactProduct = exactRow && productMatchesFilters(
        exactRow.product,
        options.priceListId,
        options.onlyWithPrice,
        options.onlyWithStock
      ) ? exactRow.product : null

      const transaction = db.transaction(STORE_PRODUCTS, 'readonly')
      const store = transaction.objectStore(STORE_PRODUCTS)
      const initialItems = offset === 0 && exactProduct ? [exactProduct] : []
      const exactProductId = exactProduct ? parseInt(exactProduct.id, 10) : null

      const range = IDBKeyRange.bound([parsedSellerId, ''], [parsedSellerId, '\uffff'])
      const cursorRequest = store.index('sellerName').openCursor(range)
      const items = initialItems.slice()
      let matched = 0
      let hasMore = false

      await new Promise((resolve, reject) => {
        cursorRequest.onsuccess = event => {
          const cursor = event.target.result
          if (!cursor) {
            resolve()
            return
          }

          const row = cursor.value
          const product = row.product
          const haystack = `${row.normalizedCode} ${row.normalizedName}`
          const matches = words.every(word => haystack.includes(word))
          const isExact = exactProductId !== null && parseInt(product.id, 10) === exactProductId

          if (matches && !isExact && productMatchesFilters(
            product,
            options.priceListId,
            options.onlyWithPrice,
            options.onlyWithStock
          )) {
            if (matched >= offset) {
              if (items.length < limit) {
                items.push(product)
              } else {
                hasMore = true
                resolve()
                return
              }
            }
            matched++
          }
          cursor.continue()
        }
        cursorRequest.onerror = () => reject(cursorRequest.error || new Error('No se pudo buscar productos'))
      })

      return { items, hasMore, nextOffset: matched }
    } catch (error) {
      console.warn('[CatalogStorage] Error buscando productos:', error)
      return { items: [], hasMore: false, nextOffset: 0, error: true }
    }
  },
}

export default CatalogStorage
